import { motion } from "framer-motion";
import {
    TriangleAlert,
    Zap,
    ShieldCheck,
    Download,
    MonitorSmartphone,
    Star,
} from "lucide-react";
import BuyButton from "./CtaButton";
import Logo from "./Logo";
import { useCountdown, pad } from "../lib/useCountdown";
import { PRICE } from "../config";

const lineReveal = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: {
            duration: 0.9,
            ease: [0.22, 1, 0.36, 1],
            delay: 0.15 + i * 0.16,
        },
    }),
};

const PERKS = [
    [ShieldCheck, "Secure Payment"],
    [Zap, "Instant Access"],
    [Download, "Downloadable"],
    [MonitorSmartphone, "Mobile + Laptop"],
];

export default function Hero() {
    const { h, m } = useCountdown();

    return (
        <section
            data-testid="hero-section"
            className="grain relative overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
            >
                <div className="absolute -top-24 left-1/2 h-[380px] w-[560px] -translate-x-1/2 rounded-full bg-emerald-100/70 blur-3xl" />
                <div className="absolute bottom-[-20%] right-[-10%] h-[300px] w-[300px] rounded-full bg-amber-100/60 blur-3xl" />
            </div>

            <div className="relative mx-auto flex max-w-3xl flex-col items-center px-4 pb-16 pt-8 text-center sm:px-6 sm:pt-12 lg:pb-20">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Logo size={40} />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="mt-5 flex flex-col items-center gap-2 sm:flex-row sm:gap-3"
                >
                    <span
                        data-testid="hero-countdown-pill"
                        className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-rose-700 sm:text-xs"
                    >
                        <TriangleAlert size={12} />
                        Sale ends in {pad(h)}:{pad(m)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-yellow-300 bg-gradient-to-r from-yellow-100 to-amber-100 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-yellow-800 sm:text-xs">
                        💥 Mega Sale 2026 — Budget Books Bundle 🔥
                    </span>
                </motion.div>

                <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.4rem]">
                    <span className="block overflow-hidden pb-1">
                        <motion.span
                            className="block"
                            variants={lineReveal}
                            initial="hidden"
                            animate="show"
                            custom={0}
                        >
                            Get{" "}
                            <span className="text-emerald-600">
                                12,000+ Premium eBooks
                            </span>
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden pb-2">
                        <motion.span
                            className="block"
                            variants={lineReveal}
                            initial="hidden"
                            animate="show"
                            custom={1}
                        >
                            at Price of{" "}
                            <span className="text-emerald-600">
                                2 burgers 🍔
                            </span>
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.7 }}
                    className="mt-5 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base"
                >
                    Why pay ₹499 for one book when you can unlock an entire
                    knowledge vault — Business, AI, Marketing, Finance,
                    Self-Growth &amp; 11+ more categories —{" "}
                    <span className="font-semibold text-emerald-700">
                        today only
                    </span>
                    .
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.85 }}
                    data-testid="hero-flash-price"
                    className="mt-6 flex flex-wrap items-center justify-center gap-2.5"
                >
                    <span className="text-sm font-semibold text-slate-700 sm:text-base">
                        Today's Flash Price:
                    </span>
                    <span className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
                        ₹{PRICE.current}
                    </span>
                    <span className="text-sm text-slate-400 line-through sm:text-base">
                        ₹{PRICE.original.toLocaleString("en-IN")}
                    </span>
                    <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-extrabold text-red-600">
                        -{PRICE.off}% OFF
                    </span>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.95 }}
                    className="mt-10 w-full"
                >
                    <div
                        data-testid="hero-video"
                        className="relative mx-auto aspect-video w-full max-w-2xl overflow-hidden rounded-[1.5rem] shadow-glow ring-1 ring-emerald-100"
                    >
                        <iframe
                            src="https://www.youtube.com/embed/5cWkOjTFr6E?rel=0"
                            title="Unlock 12,000+ Bestselling eBooks & Transform Your Life Today!"
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="absolute inset-0 h-full w-full"
                        />
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 1.05 }}
                    className="mt-9 flex w-full flex-col items-center gap-3"
                >
                    <BuyButton
                        testId="hero-primary-cta"
                        className="!bg-emerald-600 !bg-none !text-white !shadow-emerald-600/30 hover:!bg-emerald-700 w-full sm:w-auto"
                    >
                        🔥 Grab Bundle Now @ ₹{PRICE.current}
                    </BuyButton>
                    <p className="flex items-center gap-2 text-xs font-semibold text-slate-600 sm:text-sm">
                        <ShieldCheck size={15} className="text-emerald-600" />
                        Money-Back Guarantee — full refund within 24 hours of
                        purchase
                    </p>
                </motion.div>

                <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                    className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5"
                >
                    {PERKS.map(([Icon, label]) => (
                        <li
                            key={label}
                            className="flex items-center gap-1.5 text-xs font-medium text-slate-700 sm:text-sm"
                        >
                            <Icon size={14} className="text-emerald-600" />
                            {label}
                        </li>
                    ))}
                </motion.ul>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.3 }}
                    className="mt-5 flex items-center gap-2 text-xs text-slate-600 sm:text-sm"
                >
                    <span className="flex text-emerald-500">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                                key={i}
                                size={14}
                                fill="currentColor"
                                strokeWidth={0}
                            />
                        ))}
                    </span>
                    Rated 4.8/5 by 2,049 students
                </motion.p>
            </div>
        </section>
    );
}
